import { useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Field } from "./Field";
import { Segmented, YES_NO, TRANSPORT } from "./Segmented";
import { QuoteResult } from "./QuoteResult";
import { useConfig } from "../context/BrandContext";
import { api, errorField, errorMessage } from "../lib/api";

const INITIAL = { pickup_zip: "", delivery_zip: "", item_type: "sedan", transport_type: "open", operable: true, expedited: false };
const zipOk = (z) => /^\d{5}$/.test(z);

export const QuoteWidget = () => {
  const { item_types } = useConfig();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setResult(null); setErrors((e) => ({ ...e, [k]: undefined, form: undefined })); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!zipOk(form.pickup_zip)) errs.pickup_zip = "Enter a valid 5-digit ZIP.";
    if (!zipOk(form.delivery_zip)) errs.delivery_zip = "Enter a valid 5-digit ZIP.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/quote", form);
      setResult(data);
    } catch (err) {
      const f = errorField(err);
      setErrors(f ? { [f]: errorMessage(err) } : { form: errorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const zipInput = (k, testid, ph) => (
    <Input id={k} inputMode="numeric" maxLength={5} placeholder={ph} value={form[k]} data-testid={testid}
      onChange={(e) => set(k)(e.target.value.replace(/\D/g, ""))} className={`field-input font-mono ${errors[k] ? "border-red-500" : ""}`} />
  );

  return (
    <div id="quote" className="panel relative shadow-[0_24px_60px_-24px_rgba(15,23,42,.35)] scroll-mt-28" data-testid="quote-widget">
      <div className="absolute -top-px left-0 right-0 h-1 bg-amber-500" />
      <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="font-display font-extrabold uppercase text-slate-900 text-xl sm:text-2xl tracking-wide">Instant Quote</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 mt-0.5">Free · No signup · No upfront payment</div>
        </div>
        <span className="hidden sm:inline-block w-2 h-2 rounded-full bg-emerald-400 pulse-ring" />
      </div>
      {result ? (
        <QuoteResult result={result} onReset={() => setResult(null)} />
      ) : (
        <form onSubmit={submit} className="p-5 sm:p-6 space-y-4" noValidate data-testid="quote-form">
          <div className="grid grid-cols-2 gap-3">
            <Field id="pickup_zip" label="Pickup ZIP" error={errors.pickup_zip}>{zipInput("pickup_zip", "quote-input-pickup-zip", "83605")}</Field>
            <Field id="delivery_zip" label="Delivery ZIP" error={errors.delivery_zip}>{zipInput("delivery_zip", "quote-input-delivery-zip", "90210")}</Field>
          </div>
          <Field id="item_type" label="Vehicle / Cargo type">
            <Select value={form.item_type} onValueChange={set("item_type")}>
              <SelectTrigger className="field-input" data-testid="quote-select-item-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-300 text-slate-900 rounded-none">
                {item_types.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`quote-item-option-${t.value}`}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field id="transport_type" label="Transport"><Segmented value={form.transport_type} onChange={set("transport_type")} options={TRANSPORT} testid="quote-transport" /></Field>
            <Field id="operable" label="Operable?"><Segmented value={form.operable} onChange={set("operable")} options={YES_NO} testid="quote-operable" /></Field>
          </div>
          <label className="flex items-center justify-between gap-3 px-3 py-2.5 border border-slate-200 bg-white cursor-pointer" htmlFor="quote-expedited">
            <span>
              <span className="font-display uppercase tracking-wider text-sm font-semibold text-slate-900">Expedited</span>
              <span className="block text-xs text-slate-500">Priority dispatch, faster delivery</span>
            </span>
            <Switch id="quote-expedited" checked={form.expedited} onCheckedChange={set("expedited")} data-testid="quote-switch-expedited" className="data-[state=checked]:bg-amber-500" />
          </label>
          {errors.form && <p className="text-sm text-red-600" data-testid="quote-error">{errors.form}</p>}
          <button type="submit" disabled={loading} className="btn-amber w-full h-12 text-base" data-testid="quote-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get instant estimate <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      )}
    </div>
  );
};
